<template>
  <b-modal
    id="contactForm"
    modal-class="modal-cover"
    v-model="isVisible"
    hide-backdrop
    hide-footer
    content-class="shadow-lg"
    centered
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-body">
          <div class="trip-login-wrap" dir="rtl">
            <h3>{{ t('contactFormTitle') }}</h3>
            <p class="text-muted mb-4">{{ t('contactFormSubtitle') }}</p>
            
            <form @submit.prevent="handleSubmit">
              <div class="mt-3">
                <b-form-input
                  v-model="formData.name"
                  :placeholder="t('contactFormFullName')"
                  class="border-0 shadow text-right"
                  size="lg"
                  required
                />
              </div>
              <div class="mt-3">
                <b-form-input
                  v-model="formData.phone"
                  type="tel"
                  :placeholder="t('contactFormPhoneLabel')"
                  class="border-0 shadow text-right"
                  size="lg"
                  required
                  autocomplete="tel"
                />
              </div>
              <div class="mt-3">
                <b-form-input
                  v-model="formData.email"
                  type="email"
                  :placeholder="t('contactFormEmailLabel')"
                  class="border-0 shadow text-right"
                  size="lg"
                  required
                />
              </div>
              <div class="mt-3">
                <b-form-textarea
                  v-model="formData.message"
                  :placeholder="t('contactFormHelpPlaceholder')"
                  class="border-0 shadow text-right"
                  rows="4"
                  required
                />
              </div>
              <div class="mt-3 text-center">
                <b-button
                  type="submit"
                  variant="warning"
                  class="px-5 py-3 rounded-pill font-weight-bold"
                  :disabled="isLoading"
                >
                  <span v-if="isLoading" class="spinner-border spinner-border-sm mr-2"></span>
                  {{ isLoading ? t('contactFormSending') : t('contactFormSendMessage') }}
                </b-button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';
import contactApi from '@/api/contact';
import { isValidContactPhone } from '@/common/validateContactPhone';
import { translationMixin } from '@/mixins/translationMixin';

export default {
  name: 'ContactForm',
  mixins: [translationMixin],
  props: ["value"],
  data() {
    return {
      formData: {
        name: '',
        email: '',
        phone: '',
        message: '',
        source: ''
      },
      isLoading: false
    };
  },
  computed: {
    inerModalParameters() {
      return this.$store.getters.inerModalParameters || this.$store.getters['PagePopups/inerModalParameters'];
    },
    isVisible: {
      set(val) {
        this.$emit("input", val);
      },
      get() {
        return this.value;
      }
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        // When modal opens, get source from inerModalParameters
        if (this.inerModalParameters && this.inerModalParameters.source) {
          this.formData.source = this.inerModalParameters.source;
        }
      } else {
        // Reset source when modal closes
        this.formData.source = '';
      }
    }
  },
  methods: {
    ...mapActions(['showModalWithParameters']),
    
    async handleSubmit() {
      if (!this.validateForm()) {
        return;
      }
      
      this.isLoading = true;
      
      try {
        const success = await contactApi.submitContactForm(this.formData);
        
        if (success) {
          this.showModalWithParameters({
            errorNumber: 'contact_form_success',
            type: 'textConverter'
          });
          this.resetForm();
          this.isVisible = false;
        }
      } catch (error) {
        console.error('Contact form submission error:', error);
        this.showModalWithParameters({
          errorNumber: 'contact_form_error',
          type: 'textConverter'
        });
      } finally {
        this.isLoading = false;
      }
    },
    
    validateForm() {
      if (!this.formData.name.trim()) {
        this.showModalWithParameters({
          errorNumber: 'name_required',
          type: 'textConverter'
        });
        return false;
      }

      const phoneTrimmed = (this.formData.phone || '').trim();
      if (!phoneTrimmed) {
        this.showModalWithParameters({
          errorNumber: 'phone_required',
          type: 'textConverter'
        });
        return false;
      }
      if (!isValidContactPhone(phoneTrimmed)) {
        this.showModalWithParameters({
          errorNumber: 'phone_invalid',
          type: 'textConverter'
        });
        return false;
      }

      if (!this.validateEmail(this.formData.email)) {
        return false;
      }

      if (!this.formData.message.trim()) {
        this.showModalWithParameters({
          errorNumber: 'message_required',
          type: 'textConverter'
        });
        return false;
      }
      
      return true;
    },
    
    validateEmail(email) {
      const trimmedEmail = email.trim();
      
      if (trimmedEmail.includes(' ')) {
        this.showModalWithParameters({
          errorNumber: 'invalid_email_spaces',
          type: 'textConverter'
        });
        return false;
      }
      
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        this.showModalWithParameters({
          errorNumber: 'invalid_email_format',
          type: 'textConverter'
        });
        return false;
      }
      
      return true;
    },
    
    resetForm() {
      this.formData = {
        name: '',
        email: '',
        phone: '',
        message: ''
      };
    }
  }
};
</script>

<style scoped>
.modal-body {
  position: relative;
  flex: 1 1 auto;
  padding: 45px 55px;
  border: none;
}

.modal-dialog {
  max-width: 400px;
  margin: 1.75rem auto !important;
  margin-top: 10% !important;
}

.modal-content {
  background: #FFFFFF;
  box-shadow: 4px 4px 4px rgb(0 0 0 / 10%);
  border-radius: 25px;
  border: none;
}

.trip-login-wrap {
  text-align: center;
}

.trip-login-wrap h3 {
  font-weight: 800;
  font-size: 18px;
  line-height: 21px;
  color: #2E2E3A;
  margin-bottom: 15px;
  text-align: center;
}

.trip-login-wrap p {
  font-size: 14px;
  color: #6c757d;
  margin-bottom: 20px;
}

.form-control {
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 14px;
}

.form-control:focus {
  border-color: #40C4C2;
  box-shadow: 0 0 0 0.2rem rgba(64, 196, 194, 0.25);
}

.btn-warning {
  background: linear-gradient(135deg, #40C4C2 0%, #2A9D9B 100%);
  border: none;
  color: white;
}

.btn-warning:hover {
  background: linear-gradient(135deg, #2A9D9B 0%, #40C4C2 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(64, 196, 194, 0.4);
}

.btn-warning:disabled {
  opacity: 0.7;
  transform: none;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
</style>

